import {createGenericContext} from "./createGenericContext";

import CompileStore from "./compileStore";
import UserInputStore from "./userInputStore";
import CodeStore, {SaveType, SaveFunction} from "./codeStore";
import TheaterStore from "./theaterStore";
import UiStore from "./uiStore";
import FeatureFlagStore, {FeatureFlagStoreOpts} from "./featureFlagStore";
import TestsStore from "./testsStore";
import GlobalModalStore from "./globalModalStore";

import {parse as parseYaml} from "yaml";
import {Value} from "@sinclair/typebox/value";

import {Manager} from "../components/messengerManager";
import {CsProjectTypebox} from "../@types/TypeboxTypes";
import {autorun} from "mobx";
import {TestResults, UserActorList} from "../@types/Types";
import type {Files} from "../shared/Types.d.ts";
import type {ActorList} from "../@types/Types";
import MultiplayerStore from "./multiplayerStore";

const DEFAULT_PROJECT: Files = {
	"MyStage.java": {
		name: "MyStage.java",
		value: `package StudentCode;

      import AopsTheater.*;
      import java.awt.Color;

      public class MyStage extends Stage
      {

          public MyStage()
          {
          }
      }`,
		attributes: {
			isHidden: true,
			isReadOnly: true,
		},
	},
};

export enum CsInstanceTypeEnum {
	Full = "Full", // default
	Theater = "Theater", // not yet implemented
	Editor = "Editor", // for snippets
}

/**
 * Optional callbacks that can be passed to the CsInstance constructor.
 */
const CS_INSTANCE_CONSUMER_CALLBACKS = {
	/** Callback to run once the CS instance is ready to use. */
	onCsReadyCallback: () => {},

	/**
	 * Callback to run when any tests are run. Allows consumer to access results
	 * of user-run tests.
	 */
	onTestResultsCallback: (
		projectName: string | null,
		testResults: TestResults
	) => {},

	/**
	 * Callback to run when there is a change in compilation error status.
	 * To be used for displaying the sidebar's toggle theater button.
	 * Note: not currently implemented.
	 */
	toggleTheaterButtonErrorCallback: (hasError: boolean) => {},
	/**
	 * Callback to run when there is a change in theater active status.
	 * To be used for displaying the sidebar's toggle theater button.
	 * Note: not currently implemented.
	 */
	toggleTheaterButtonActiveCallback: (hasActive: boolean) => {},
} as const;
type CsInstanceConsumerCallbacksOpts = Partial<
	typeof CS_INSTANCE_CONSUMER_CALLBACKS
>;

/**
 * The main class that holds all the mobx stores and messenger manager.  This is
 * our main export for the CS package.
 *
 * Note: There may be a future in which there are multiple instances of this
 * class running on the same page.
 */
export default class CsInstance {
	private _codeStore: CodeStore;
	private _compileStore: CompileStore;
	private _userInputStore: UserInputStore;
	private _theaterStore: TheaterStore;
	private _uiStore: UiStore;
	private _featureFlagStore: FeatureFlagStore;
	private _testsStore: TestsStore;
	private _globalModalStore: GlobalModalStore;
	private _multiplayerStore: MultiplayerStore;

	private _manager = new Manager(this);
	private _serverUrl = "";
	private _securityToken = "";

	// To keep track of what subcomponents to show
	private _useTheater = false;
	private _useEditor = false;

	// list of cleanup handlers to run when the instance is destroyed
	private _cleanupHandlers: (() => void)[] = [];

	// Callbacks which can be overwritten by consumers in props.consumerCallbacks
	private _consumerCallbacks = {...CS_INSTANCE_CONSUMER_CALLBACKS};

	private _currentVersion: number | null = null;

	/**
	 * Represents the entirety of state necessary for the CS classroom. Make sure
	 * to call destroy() before discarding the instance.
	 * @param props.serverUrl - URL of the CS compilation server
	 * @param props.csInstanceType - Type of CS instance to create.  Defaults to 'Full'.
	 * @param props.featureOpts - Feature flags to enable/disable.
	 * @param props.securityToken - Security token to validate all server requests.
	 * @param props.onCsReadyCallback - deprecated, use props.consumerCallbacks
	 * @param props.minorSaveFunction - Callback to run when a minor save is
	 * 	 triggered. A minor save is one that overwrites the previous entry.
	 * @param props.majorSaveFunction - Callback to run when a major save is
	 * 	 triggered. A major save is on that creates a new entry.
	 * 	 Currently this is only triggered when a project is switched or compiled.
	 * @param props.consumerCallbacks - Object containing any callbacks to be used
	 *   by the consumer.
	 */
	constructor(props: {
		serverUrl: string;
		csInstanceType?: CsInstanceTypeEnum;
		featureOpts?: FeatureFlagStoreOpts;
		securityToken?: string;
		onCsReadyCallback?: () => void; // kept for backwards compatibility
		consumerCallbacks?: CsInstanceConsumerCallbacksOpts;
		minorSaveFunction?: SaveFunction;
		majorSaveFunction?: SaveFunction;
		sendStateFunction?: (states: ActorList) => void; //  TODO - better type definition
	}) {
		this._featureFlagStore = new FeatureFlagStore({
			opts: props.featureOpts,
			csInstance: this,
		});
		this._serverUrl = props.serverUrl;
		this._securityToken = props.securityToken || "";
		this.setCsInstanceType(props.csInstanceType || CsInstanceTypeEnum.Full);

		this._codeStore = new CodeStore({
			csInstance: this,
			serverUrl: props.serverUrl,
			minorSaveFunction: props.minorSaveFunction,
			majorSaveFunction: props.majorSaveFunction,
		});
		this._compileStore = new CompileStore({
			csInstance: this,
			serverUrl: props.serverUrl,
		});
		this._userInputStore = new UserInputStore({csInstance: this});
		this._theaterStore = new TheaterStore();
		this._uiStore = new UiStore({csInstance: this});
		this._testsStore = new TestsStore();
		this._globalModalStore = new GlobalModalStore({csInstance: this});
		this._multiplayerStore = new MultiplayerStore({
			csInstance: this,
			sendStates: props.sendStateFunction,
		});

		// callbacks: backwards compatibility
		this._consumerCallbacks.onCsReadyCallback =
			props.onCsReadyCallback || this._consumerCallbacks.onCsReadyCallback;
		(
			Object.keys(
				CS_INSTANCE_CONSUMER_CALLBACKS
			) as (keyof typeof CS_INSTANCE_CONSUMER_CALLBACKS)[]
		).forEach(
			<K extends keyof typeof CS_INSTANCE_CONSUMER_CALLBACKS>(key: K) => {
				const propValue = props.consumerCallbacks?.[key];
				if (propValue) {
					this._consumerCallbacks[key] = propValue;
				}
			}
		);

		this._setupReactions();

		// Load the default project

		this._compileStore.compileCode("preloadcheerpj", DEFAULT_PROJECT);
	}

	destroy() {
		this._cleanupHandlers.forEach((handler) => handler());
	}

	private _setupReactions() {
		const reactions = [
			// load new JAR into messenger whenever a new JAR is available
			() => {
				if (this._compileStore.versionedJarData) {
					const [_, newVersion] = this._compileStore.versionedJarData;
					if (this._currentVersion !== newVersion) {
						this._currentVersion = newVersion;
						this._manager.prepNewVersionedJarMessenger(
							...this._compileStore.versionedJarData
						);
						this._theaterStore.resetTheaterState();
						this._multiplayerStore.resetTrackedUuid();
					}
				}
			},
		];
		this._cleanupHandlers = reactions.map((callback) => autorun(callback));
	}

	/**
	 * Parses a CS YAML and loads it into this CS instance.
	 * If the YAML is invalid, throw an error and do nothing.
	 * @param yaml The Yaml string correlating to a CS Project to be parsed and displayed in the IDE.
	 * @returns The name of the CS Project inputted.
	 */
	intakeProjectYaml(yaml: string): string {
		let yamlObj;
		try {
			// validate the yaml using TypeBox
			yamlObj = parseYaml(yaml);
		} catch (e) {
			throw new Error("Malformed Yaml");
		}

		if (!Value.Check(CsProjectTypebox, yamlObj)) {
			console.error("Here is the parsed result of the input yaml:", yamlObj);
			throw new Error(
				"Invalid activity YAML" +
					JSON.stringify([...Value.Errors(CsProjectTypebox, yamlObj)], null, 2)
			);
		}

		// drop it into CodeStore
		this._codeStore.cacheProjectTemplate(yamlObj);

		if (yamlObj.multiplayerClass) {
			this._multiplayerStore.isMultiplayer = true;
			this._multiplayerStore.trackedClass = yamlObj.multiplayerClass;
		}

		return yamlObj.name;
	}

	getOrCreateMessenger = this._manager.getOrCreateMessenger.bind(this._manager);

	/**
	 * Request that the theater be updated according to the given JSON string.
	 *
	 * @param updateJson - JSON string representing the update to the theater
	 * 		Should be of a form like {actors: [{uuid: string, x: number}, ...]}
	 */
	updateMultiplayerState(updateJson: UserActorList | null) {
		if (!updateJson) {
			return;
		}

		this._multiplayerStore.updateOtherActorFrames(updateJson);
	}

	defaultConsoleLogCallback(s: string) {
		this._theaterStore.clearLog();
	}
	defaultConsoleInputCallback(s: number) {
		this._theaterStore.studentInputRequested = true;
	}

	private setCsInstanceType(csInstanceType: CsInstanceTypeEnum) {
		this._useTheater = [
			CsInstanceTypeEnum.Theater,
			CsInstanceTypeEnum.Full,
		].includes(csInstanceType);
		this._useEditor = [
			CsInstanceTypeEnum.Editor,
			CsInstanceTypeEnum.Full,
		].includes(csInstanceType);
	}

	/**
	 * Changes the compilation server URL for this CS instance
	 * @param url - URL of the CS compilation server
	 */
	setServerUrl(url: string) {
		this._serverUrl = url;
		this._codeStore.serverUrl = url;
		this._compileStore.serverUrl = url;
	}

	/**
	 * Changes the CS project to the given project, updating the
	 * state of all components attached to this CS instance.
	 * @param projectName - Name of the project to switch to
	 * @param shouldCompile - Whether to recompile the project after switching.
	 * @param codeState - The state of the code editor to switch to
	 * @param options
	 */
	switchProject(
		projectName: string,
		shouldCompile = true,
		codeState?: string,
		options?: {
			useTests?: boolean;
		}
	) {
		this._theaterStore.toggleShowManifest(false);
		this._codeStore.switchProject(
			projectName,
			shouldCompile,
			codeState ? JSON.parse(codeState) : null
		);
		if (options?.useTests !== undefined) {
			this._testsStore.shouldRunTests = options.useTests;
		} else {
			this._testsStore.shouldRunTests = true;
		}
	}

	saveCodeState() {
		this._codeStore.saveProject(SaveType.Manual);
	}

	get codeState() {
		return {
			fileData: this._codeStore.fileMap,
			filesOpen: this._codeStore.filesOpen,
			focusedFile: this._codeStore.focusedFile,
		};
	}

	get serverUrl() {
		return this._serverUrl;
	}

	get securityToken() {
		return this._securityToken;
	}

	set securityToken(token: string) {
		this._securityToken = token;
	}

	get codeStore() {
		return this._codeStore;
	}

	get compileStore() {
		return this._compileStore;
	}

	get userInputStore() {
		return this._userInputStore;
	}

	get theaterStore() {
		return this._theaterStore;
	}

	get uiStore() {
		return this._uiStore;
	}

	get featureFlagStore() {
		return this._featureFlagStore;
	}

	get testsStore() {
		return this._testsStore;
	}

	get globalModalStore() {
		return this._globalModalStore;
	}

	get multiplayerStore() {
		return this._multiplayerStore;
	}

	get manager() {
		return this._manager;
	}

	get useTheater() {
		return this._useTheater;
	}

	get useEditor() {
		return this._useEditor;
	}

	get onCsReadyCallback() {
		return this._consumerCallbacks.onCsReadyCallback;
	}
	get onTestResultsCallback() {
		return this._consumerCallbacks.onTestResultsCallback;
	}
	get toggleTheaterButtonErrorCallback() {
		return this._consumerCallbacks.toggleTheaterButtonErrorCallback;
	}
	get toggleTheaterButtonActiveCallback() {
		return this._consumerCallbacks.toggleTheaterButtonActiveCallback;
	}

	set minorSaveFunction(
		minorSaveFunction: (projectName: string, codeState: string) => void
	) {
		this.codeStore.minorSaveFunction = minorSaveFunction;
	}
	set majorSaveFunction(
		majorSaveFunction: (projectName: string, codeState: string) => void
	) {
		this.codeStore.majorSaveFunction = majorSaveFunction;
	}

	set sendStateFunction(sendStateFunction: (states: ActorList) => void) {
		this._multiplayerStore.sendStates = sendStateFunction;
	}
}

/**
 * React Context which allows subcomponents to easily access the top-level
 * CsInstance.  Its type needs to be `CsInstance | null` to allow for
 * initialization here, even though it should never be used without a Provider
 * with a valid CsInstance value.
 */

export const [useCsContext, CsProvider] = createGenericContext<CsInstance>();
